import * as Sentry from "@sentry/nextjs";
import { AxiosError } from "axios";

type LogLevel = "info" | "warn" | "error";

interface LogContext {
  [key: string]: unknown;
}

interface SerializedError {
  name: string;
  message: string;
  stack?: string;
  code?: string;
  status?: number;
  statusText?: string;
  responseData?: unknown;
}

function isErrorLike(value: unknown): value is { name?: string; message?: string; stack?: string } {
  return (
    value !== null &&
    typeof value === "object" &&
    ("message" in value || "stack" in value)
  );
}

function isAxiosError(value: unknown): value is AxiosError {
  return (
    value !== null &&
    typeof value === "object" &&
    "isAxiosError" in value &&
    (value as AxiosError).isAxiosError === true
  );
}

function serializeError(value: unknown): SerializedError {
  // Handle Axios errors specifically
  if (isAxiosError(value)) {
    return {
      name: value.name ?? "AxiosError",
      message: value.message ?? "Unknown Axios error",
      stack: value.stack,
      code: value.code,
      status: value.response?.status,
      statusText: value.response?.statusText,
      responseData: value.response?.data,
    };
  }

  // Handle standard errors
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  // Handle error-like objects
  if (isErrorLike(value)) {
    const err = value as { name?: string; message?: string; stack?: string };
    return {
      name: err.name ?? "Error",
      message: err.message ?? String(value),
      stack: err.stack,
    };
  }

  // Fallback for unknown error types
  return {
    name: "UnknownError",
    message: String(value),
  };
}

function serializeContext(context?: LogContext): LogContext | string {
  if (!context) return "";

  const serialized: LogContext = {};
  for (const [key, value] of Object.entries(context)) {
    if (isAxiosError(value) || value instanceof Error || isErrorLike(value)) {
      serialized[key] = serializeError(value);
    } else {
      serialized[key] = value;
    }
  }
  return serialized;
}

function log(level: LogLevel, message: string, context?: LogContext) {
  const timestamp = new Date().toISOString();

  if (process.env.NODE_ENV === "development") {
    const logFn = level === "error" ? console.error : level === "warn" ? console.warn : console.log;
    logFn(`[${timestamp}] [${level.toUpperCase()}] ${message}`, serializeContext(context));
  }

  if (level === "error" && context?.error instanceof Error) {
    Sentry.captureException(context.error, {
      extra: { message, ...context },
    });
  } else if (level === "error") {
    Sentry.captureMessage(message, {
      level: "error",
      extra: context,
    });
  }
}

export const logger = {
  info: (message: string, context?: LogContext) => log("info", message, context),
  warn: (message: string, context?: LogContext) => log("warn", message, context),
  error: (message: string, context?: LogContext) => log("error", message, context),
};
